import { useEffect, useState } from 'react';
import { Award, AlertCircle, Plus } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { complianceApi, ApiError } from '@solara/api-client';
import type { IsoCertificationDto } from '@solara/api-client';

export default function IsoCertificationsPage() {
  const { t } = useTranslation('p1');
  const [items, setItems] = useState<IsoCertificationDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<IsoCertificationDto>>({ standard: 'ISO_27001' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setItems(await complianceApi.iso());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function add() {
    if (!draft.certificateNumber || !draft.issuer || !draft.effectiveFrom || !draft.effectiveTo) {
      setError(t('common.fieldRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await complianceApi.createIso({
        standard: draft.standard ?? 'ISO_27001',
        certificateNumber: draft.certificateNumber,
        issuer: draft.issuer,
        effectiveFrom: draft.effectiveFrom,
        effectiveTo: draft.effectiveTo,
        scope: draft.scope,
      });
      setDraft({ standard: 'ISO_27001' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('compliance.isoTitle')}</h1>
        <p className="text-fg-muted text-sm">{t('compliance.isoSubtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            {t('compliance.iso.certsCount', { count: items.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('compliance.iso.noCerts')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((i) => (
                <li key={i.id} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <p className="font-medium">{i.standard}</p>
                    <p className="text-xs text-fg-muted">
                      {i.issuer} · #{i.certificateNumber}
                      {i.scope && <span> · {i.scope}</span>}
                    </p>
                  </div>
                  <Badge
                    tone={new Date(i.effectiveTo) < new Date() ? 'danger' : 'neutral'}
                    className="text-[10px]"
                  >
                    {t('compliance.iso.validUntil')}{' '}
                    {new Date(i.effectiveTo).toLocaleDateString()}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {t('compliance.iso.addCert')}
          </CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <select
            className="rounded border border-border bg-surface px-3 py-2 text-sm"
            value={draft.standard ?? 'ISO_27001'}
            onChange={(e) => setDraft({ ...draft, standard: e.target.value })}
          >
            <option value="ISO_27001">ISO 27001</option>
            <option value="ISO_22301">ISO 22301</option>
            <option value="SOC_2">SOC 2</option>
          </select>
          <Input
            placeholder={t('compliance.iso.certNumber')}
            value={draft.certificateNumber ?? ''}
            onChange={(e) => setDraft({ ...draft, certificateNumber: e.target.value })}
          />
          <Input
            placeholder={t('compliance.iso.certifier')}
            value={draft.issuer ?? ''}
            onChange={(e) => setDraft({ ...draft, issuer: e.target.value })}
          />
          <Input
            placeholder={`${t('compliance.iso.standard')} ${t('common.optional')}`}
            value={draft.scope ?? ''}
            onChange={(e) => setDraft({ ...draft, scope: e.target.value })}
          />
          <Input
            type="date"
            placeholder={t('compliance.iso.issuedAt')}
            value={draft.effectiveFrom ?? ''}
            onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })}
          />
          <Input
            type="date"
            placeholder={t('compliance.iso.validUntil')}
            value={draft.effectiveTo ?? ''}
            onChange={(e) => setDraft({ ...draft, effectiveTo: e.target.value })}
          />
          <div className="sm:col-span-2">
            <Button onClick={add} disabled={busy}>
              {busy ? t('actions.saving') : t('compliance.iso.addCert')}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
